import { useMemo } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, GizmoHelper, GizmoViewport } from '@react-three/drei';
import * as THREE from 'three';
import type { InteractionSurfaceResponse } from './types';

interface Props {
  surface: InteractionSurfaceResponse | undefined;
  Pu: number;
  Mu_x: number;
  Mu_y: number;
  /** which envelope to render: nominal or design (with phi) */
  envelope?: 'nominal' | 'design';
  /** mesh visualization mode */
  view?: 'wireframe' | 'mesh' | 'points';
  /** Bounding-box dimensions (cm) — used as a stable reference for the
   *  visual scale so that, for a given L1xL2, the rectangular and L
   *  solids render at the *same* scale (L appears smaller because it has
   *  less material). Without this, computeScales would normalize each
   *  surface to its own max and every section would look the same size. */
  L1: number;
  L2: number;
  fc: number;
}

const COLOR_NOMINAL = '#94A3B8'; // slate-400
const COLOR_DESIGN = '#8B5CF6'; // brand-violet
const COLOR_DEMAND = '#F59E0B'; // accent
const COLOR_AXIS_X = '#22C55E'; // success (Mx)
const COLOR_AXIS_Y = '#3B82F6'; // brand-alt (My)
const COLOR_AXIS_Z = '#EF4444'; // danger (P)

/**
 * Build a triangulated mesh (closed in theta direction) from the surface grid.
 * Coordinates: x = Mx, y = My, z = Pn. Scaled by `scale` so the camera frames
 * the geometry well (the actual values are in kgf and kgf-cm, which would be
 * huge in scene units).
 */
function useGeometry(
  s: InteractionSurfaceResponse | undefined,
  envelope: 'nominal' | 'design',
  scale: { M: number; P: number },
): {
  positions: Float32Array;
  indices: Uint32Array;
  edges: Float32Array;
} | null {
  return useMemo(() => {
    if (!s) return null;
    const { n_angular: A, n_points: N } = s;
    const Mx = envelope === 'nominal' ? s.Mx_n : s.phi_Mx;
    const My = envelope === 'nominal' ? s.My_n : s.phi_My;
    const Pn = envelope === 'nominal' ? s.Pn : s.phi_Pn;

    const positions = new Float32Array(A * N * 3);
    for (let i = 0; i < A; i++) {
      for (let j = 0; j < N; j++) {
        const k = i * N + j;
        positions[3 * k + 0] = Mx[k] * scale.M;
        positions[3 * k + 1] = My[k] * scale.M;
        positions[3 * k + 2] = Pn[k] * scale.P;
      }
    }

    // Indices: connect (i, j) -- (i+1, j) -- (i, j+1) -- (i+1, j+1) as 2 triangles.
    // Wrap i in [0, A) modulo A.
    const tri: number[] = [];
    for (let i = 0; i < A; i++) {
      const i2 = (i + 1) % A;
      for (let j = 0; j < N - 1; j++) {
        const a = i * N + j;
        const b = i2 * N + j;
        const c = i * N + (j + 1);
        const d = i2 * N + (j + 1);
        tri.push(a, b, c, c, b, d);
      }
    }
    const indices = new Uint32Array(tri);

    // Wireframe edges (only "ring" lines for clarity, plus a few longitudinal)
    const edgePts: number[] = [];
    for (let i = 0; i < A; i++) {
      const i2 = (i + 1) % A;
      for (let j = 0; j < N; j++) {
        const a = i * N + j;
        const b = i2 * N + j;
        const c = i * N + (j + 1);
        if (j < N - 1) {
          edgePts.push(
            positions[3 * a],
            positions[3 * a + 1],
            positions[3 * a + 2],
            positions[3 * c],
            positions[3 * c + 1],
            positions[3 * c + 2],
          );
        }
        edgePts.push(
          positions[3 * a],
          positions[3 * a + 1],
          positions[3 * a + 2],
          positions[3 * b],
          positions[3 * b + 1],
          positions[3 * b + 2],
        );
      }
    }

    return { positions, indices, edges: new Float32Array(edgePts) };
  }, [s, envelope, scale.M, scale.P]);
}

function computeScales(L1: number, L2: number, fc: number): {
  M: number;
  P: number;
} {
  // Stable reference based on the gross bbox capacity. Two sections with the
  // same L1, L2, f'c share this reference — so a rect (uses the full bbox)
  // renders LARGER than an L (uses only a fraction of the bbox) at the same
  // dimensions. Without this, every surface would auto-fit to the viewport
  // and rectangular/L would look identical.
  //   P_ref = 0.85 * f'c * L1 * L2          (~ axial capacity if the section
  //                                            were a full rectangle)
  //   M_ref = P_ref * max(L1, L2) / 2       (~ moment capacity at half-depth)
  const Pref = 0.85 * Math.max(fc, 1) * Math.max(L1, 1) * Math.max(L2, 1);
  // Mref ~ theoretical max moment of the bbox: Pref · (max_dim / 4) — comes from
  // the moment of a uniform stress block over half the section.
  const Mref = Pref * Math.max(L1, L2) / 4.0;
  // Map the reference to ~2 in scene units; smaller actual capacities show up
  // as proportionally smaller surfaces.
  return { M: 2 / Mref, P: 2 / Pref };
}

export function PMSurface3D({
  surface,
  Pu,
  Mu_x,
  Mu_y,
  envelope = 'design',
  view = 'wireframe',
  L1,
  L2,
  fc,
}: Props) {
  const scale = useMemo(() => computeScales(L1, L2, fc), [L1, L2, fc]);
  const geom = useGeometry(surface, envelope, scale);

  // Numeric peaks for the legend overlay (shows the user that the surface
  // actually differs between section types even if the silhouette looks
  // visually similar at small dimensions).
  const peaks = useMemo(() => {
    if (!surface) return null;
    const Mx = envelope === 'nominal' ? surface.Mx_n : surface.phi_Mx;
    const My = envelope === 'nominal' ? surface.My_n : surface.phi_My;
    const Pn = envelope === 'nominal' ? surface.Pn : surface.phi_Pn;
    let Mxm = 0;
    let Mym = 0;
    let Pmx = 0;
    let Pmn = 0;
    for (const v of Mx) if (Math.abs(v) > Mxm) Mxm = Math.abs(v);
    for (const v of My) if (Math.abs(v) > Mym) Mym = Math.abs(v);
    for (const v of Pn) {
      if (v > Pmx) Pmx = v;
      if (v < Pmn) Pmn = v;
    }
    return { Mxm, Mym, Pmx, Pmn };
  }, [surface, envelope]);

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
    <Canvas
      camera={{ position: [4, 4, 4], fov: 45 }}
      style={{ width: '100%', height: '100%', background: 'var(--surface-canvas)' }}
      dpr={[1, 2]}
    >
      <ambientLight intensity={0.6} />
      <directionalLight position={[5, 5, 5]} intensity={0.8} />
      <directionalLight position={[-5, -2, -3]} intensity={0.3} />

      <ReferenceCage />
      <Axes />

      {geom && view !== 'points' && (
        <mesh>
          <bufferGeometry>
            <bufferAttribute
              attach="attributes-position"
              array={geom.positions}
              count={geom.positions.length / 3}
              itemSize={3}
            />
            <bufferAttribute attach="index" array={geom.indices} count={geom.indices.length} itemSize={1} />
          </bufferGeometry>
          <meshStandardMaterial
            color={envelope === 'nominal' ? COLOR_NOMINAL : COLOR_DESIGN}
            transparent
            opacity={view === 'mesh' ? 0.4 : 0.06}
            side={THREE.DoubleSide}
            wireframe={view === 'mesh' ? false : false}
          />
        </mesh>
      )}

      {geom && view === 'wireframe' && (
        <lineSegments>
          <bufferGeometry>
            <bufferAttribute
              attach="attributes-position"
              array={geom.edges}
              count={geom.edges.length / 3}
              itemSize={3}
            />
          </bufferGeometry>
          <lineBasicMaterial color={envelope === 'nominal' ? COLOR_NOMINAL : COLOR_DESIGN} />
        </lineSegments>
      )}

      {geom && view === 'points' && (
        <points>
          <bufferGeometry>
            <bufferAttribute
              attach="attributes-position"
              array={geom.positions}
              count={geom.positions.length / 3}
              itemSize={3}
            />
          </bufferGeometry>
          <pointsMaterial color={envelope === 'nominal' ? COLOR_NOMINAL : COLOR_DESIGN} size={0.04} sizeAttenuation />
        </points>
      )}

      <DemandPoint Pu={Pu} Mu_x={Mu_x} Mu_y={Mu_y} scale={scale} />

      <OrbitControls makeDefault enableDamping dampingFactor={0.08} />
      <GizmoHelper alignment="bottom-right" margin={[60, 60]}>
        <GizmoViewport axisColors={[COLOR_AXIS_X, COLOR_AXIS_Y, COLOR_AXIS_Z]} labelColor="white" />
      </GizmoHelper>
    </Canvas>
    {peaks && (
      <div
        style={{
          position: 'absolute',
          top: 12,
          right: 12,
          padding: '8px 10px',
          background: 'rgba(15, 23, 42, 0.78)',
          border: '1px solid rgba(148, 163, 184, 0.25)',
          borderRadius: 6,
          fontSize: 11,
          lineHeight: 1.45,
          color: '#E2E8F0',
          fontFamily: 'monospace',
          pointerEvents: 'none',
        }}
      >
        <div style={{ color: '#94A3B8', fontSize: 10, letterSpacing: '0.12em', marginBottom: 4 }}>
          PICOS DEL SOLIDO
        </div>
        <div>φPn_max&nbsp;= <span style={{ color: '#F59E0B' }}>{(peaks.Pmx / 1000).toFixed(1)} tonf</span></div>
        <div>φPn_min&nbsp;= <span style={{ color: '#F59E0B' }}>{(peaks.Pmn / 1000).toFixed(1)} tonf</span></div>
        <div>max&nbsp;|φMx|&nbsp;= <span style={{ color: '#22C55E' }}>{(peaks.Mxm / 100000).toFixed(2)} tonf·m</span></div>
        <div>max&nbsp;|φMy|&nbsp;= <span style={{ color: '#3B82F6' }}>{(peaks.Mym / 100000).toFixed(2)} tonf·m</span></div>
      </div>
    )}
    </div>
  );
}

/** Static wireframe cube at the reference scale [-2..2] in all axes.
 *  Acts as a "size reference cage": the actual envelope fills more of the
 *  cube when the section has more capacity (full rectangular bbox) and
 *  less when it has reduced area (L). Helps the user perceive size
 *  differences between section types at a glance. */
function ReferenceCage() {
  const geom = useMemo(() => {
    const s = 2.2; // slightly bigger than the surface scale (which peaks at 2)
    const verts = [
      // bottom rectangle
      [-s, -s, -s], [s, -s, -s],
      [s, -s, -s], [s, s, -s],
      [s, s, -s], [-s, s, -s],
      [-s, s, -s], [-s, -s, -s],
      // top rectangle
      [-s, -s, s], [s, -s, s],
      [s, -s, s], [s, s, s],
      [s, s, s], [-s, s, s],
      [-s, s, s], [-s, -s, s],
      // verticals
      [-s, -s, -s], [-s, -s, s],
      [s, -s, -s], [s, -s, s],
      [s, s, -s], [s, s, s],
      [-s, s, -s], [-s, s, s],
    ];
    const flat = new Float32Array(verts.flat());
    return flat;
  }, []);
  return (
    <lineSegments>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" array={geom} count={geom.length / 3} itemSize={3} />
      </bufferGeometry>
      <lineBasicMaterial color="#334155" transparent opacity={0.45} />
    </lineSegments>
  );
}

function Axes() {
  return (
    <group>
      <AxisLine color={COLOR_AXIS_X} from={[0, 0, 0]} to={[2.4, 0, 0]} />
      <AxisLine color={COLOR_AXIS_Y} from={[0, 0, 0]} to={[0, 2.4, 0]} />
      <AxisLine color={COLOR_AXIS_Z} from={[0, 0, -2.4]} to={[0, 0, 2.4]} />
    </group>
  );
}

function AxisLine({
  color,
  from,
  to,
}: {
  color: string;
  from: [number, number, number];
  to: [number, number, number];
}) {
  const points = useMemo(() => new Float32Array([...from, ...to]), [from, to]);
  return (
    <lineSegments>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" array={points} count={2} itemSize={3} />
      </bufferGeometry>
      <lineBasicMaterial color={color} linewidth={2} />
    </lineSegments>
  );
}

function DemandPoint({
  Pu,
  Mu_x,
  Mu_y,
  scale,
}: {
  Pu: number;
  Mu_x: number;
  Mu_y: number;
  scale: { M: number; P: number };
}) {
  const x = Mu_x * scale.M;
  const y = Mu_y * scale.M;
  const z = Pu * scale.P;

  // Guide line from the origin to the demand point — makes it obvious
  // where the point sits relative to (0, 0, 0) when the wireframe is sparse
  // and perspective makes the dot look "outside" the envelope.
  const lineGeom = useMemo(() => {
    const pts = new Float32Array([0, 0, 0, x, y, z]);
    return pts;
  }, [x, y, z]);

  return (
    <group>
      {/* tiny origin marker so the user can locate (M=0, P=0) at a glance */}
      <mesh position={[0, 0, 0]}>
        <sphereGeometry args={[0.03, 12, 12]} />
        <meshBasicMaterial color="#94A3B8" />
      </mesh>
      {/* guide line origin -> demand */}
      <lineSegments>
        <bufferGeometry>
          <bufferAttribute attach="attributes-position" array={lineGeom} count={2} itemSize={3} />
        </bufferGeometry>
        <lineBasicMaterial color={COLOR_DEMAND} transparent opacity={0.55} />
      </lineSegments>
      {/* demand point itself */}
      <mesh position={[x, y, z]}>
        <sphereGeometry args={[0.08, 16, 16]} />
        <meshStandardMaterial color={COLOR_DEMAND} emissive={COLOR_DEMAND} emissiveIntensity={0.4} />
      </mesh>
    </group>
  );
}
